import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { WarningDiamondIcon } from "@phosphor-icons/react";

const SEV = { high: "bg-rose-50 text-rose-700 border-rose-200", medium: "bg-amber-50 text-amber-700 border-amber-200", low: "bg-slate-50 text-slate-600 border-slate-200" };

export default function Discrepancies() {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("open");

  const load = () => api.get("/discrepancies", { params: tab === "all" ? {} : { status: tab } }).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [tab]);

  const resolve = async (id, resolution) => {
    try {
      await api.post(`/discrepancies/${id}/resolve`, { resolution });
      toast.success(`Discrepancy ${resolution.replace("_", " ")}`);
      load();
    } catch (e) { toast.error("Failed to resolve"); }
  };

  return (
    <div className="p-5 lg:p-8 space-y-5">
      <div>
        <h1 className="font-display text-2xl lg:text-3xl font-semibold text-slate-900">Discrepancy Queue</h1>
        <p className="text-sm text-slate-500 mt-1">Cross-source conflicts. Ambiguous matches are flagged, never auto-merged.</p>
      </div>

      <div className="flex gap-1 border-b border-slate-200">
        {["open", "resolved", "all"].map((t) => (
          <button key={t} data-testid={`disc-tab-${t}`} onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium capitalize border-b-2 -mb-px transition-colors ${tab === t ? "border-emerald-600 text-emerald-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}>{t}</button>
        ))}
      </div>

      <div className="space-y-3">
        {items.map((d) => (
          <div key={d.id} data-testid={`disc-${d.id}`} className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase ${SEV[d.severity]}`}>{d.severity}</span>
                  <Link to={`/sites/${d.site_id}`} className="font-medium text-slate-900 hover:text-emerald-600">{d.site_name}</Link>
                </div>
                <div className="mt-2 flex items-center gap-3 text-sm">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{d.field}</span>
                  <span className="font-mono text-slate-700">{String(d.existing_value)}</span>
                  <span className="text-slate-300">→</span>
                  <span className="font-mono text-emerald-700">{String(d.incoming_value)}</span>
                </div>
                <div className="mt-1 text-xs text-slate-400">from {d.source_name}</div>
              </div>
              {d.status === "open" ? (
                <div className="flex gap-2">
                  <button data-testid={`accept-${d.id}`} onClick={() => resolve(d.id, "accept_incoming")} className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 transition-colors">Accept incoming</button>
                  <button data-testid={`keep-${d.id}`} onClick={() => resolve(d.id, "keep_existing")} className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 transition-colors">Keep existing</button>
                  <button data-testid={`dismiss-${d.id}`} onClick={() => resolve(d.id, "dismiss")} className="rounded-lg px-3 py-1.5 text-xs font-medium text-slate-400 hover:text-rose-600 transition-colors">Dismiss</button>
                </div>
              ) : (
                <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs text-slate-500 capitalize">{(d.resolution || "resolved").replace("_", " ")}</span>
              )}
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <div className="rounded-xl border border-dashed border-slate-200 bg-white p-12 text-center">
            <WarningDiamondIcon size={32} className="mx-auto text-slate-300" />
            <p className="mt-2 text-sm text-slate-400">Queue is empty — an empty queue is safe, not dangerous.</p>
          </div>
        )}
      </div>
    </div>
  );
}
